// src/context/AuthContext.js

import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { publicApiPost } from '../utils/apiUtils';

// 1. Create the context
const AuthContext = createContext();

/**
 * Global Authentication Provider.
 * 
 * Manages user state, JWT tokens (access & refresh), auto-refresh logic,
 * activity tracking (to extend sessions), and Google OAuth integration.
 * 
 * @param {Object} props - React props
 * @param {React.ReactNode} props.children - Child components to wrap with Auth Context
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Refs for tracking timers and activity
  const refreshTimeoutRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isRefreshingRef = useRef(false);
  // Ref to prevent duplicate OAuth sync calls
  const isSyncingOAuthRef = useRef(false);

  // Activity tracking constants
  const ACTIVITY_THRESHOLD = 60 * 60 * 1000; // 1 hour in milliseconds
  const AUTO_REFRESH_BUFFER = 10 * 60 * 1000; // 10 minutes before expiry

  // =========================================================================
  // GOOGLE OAUTH — Sign in with Google via Supabase
  // =========================================================================
  /**
   * Initiates the Google OAuth login flow using Supabase.
   * Redirects the user to Google's authentication page.
   * 
   * @throws {Error} If the Supabase OAuth request fails.
   */
  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth/callback'
        }
      });
      if (error) {
        console.error('❌ Google OAuth error:', error.message);
        throw error;
      }
      // Supabase will redirect to Google, then back to redirectTo URL.
      // The onAuthStateChange listener (below) will handle the rest.
    } catch (error) {
      console.error('❌ Google sign-in failed:', error);
      throw error;
    }
  };

  // =========================================================================
  // GOOGLE OAUTH — Sync Google user with our backend after OAuth
  // =========================================================================
  /**
   * Synchronizes a successfully authenticated Google user (via Supabase)
   * with the custom SimpleCode backend to retrieve custom JWTs and user data.
   * 
   * @param {Object} supabaseUser - The user object returned by Supabase Auth.
   * @returns {Promise<Object>} The synchronized user data from the backend.
   * @throws {Error} If the backend sync fails.
   */
  const syncGoogleUser = useCallback(async (supabaseUser) => {
    // Prevent duplicate sync calls (Supabase can fire multiple events)
    if (isSyncingOAuthRef.current) return;
    isSyncingOAuthRef.current = true;

    try {
      const metadata = supabaseUser.user_metadata || {};
      const googleName = metadata.full_name || metadata.name || 'User';
      const googleEmail = supabaseUser.email;
      const authId = supabaseUser.id;
      // Extract Google profile picture URL from OAuth metadata
      const googleAvatarUrl = metadata.avatar_url || metadata.picture || '';

      // Build the sync payload — username will be null for new users (set later)
      const syncPayload = {
        authId,
        email: googleEmail,
        username: '', // Backend will set to null for new Google users
        name: googleName,
        avatar_url: googleAvatarUrl // Pass avatar URL to backend
      };

      const response = await publicApiPost('/auth/sync', syncPayload);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Backend sync failed');
      }

      // Attach avatar_url to user object (use backend value or fallback to Google metadata)
      const userWithAvatar = {
        ...data.user,
        avatar_url: data.user.avatar_url || googleAvatarUrl
      };

      // Use the existing login function to store tokens & update state
      loginUser(
        userWithAvatar,
        data.accessToken,
        data.refreshToken,
        data.accessTokenExpiry,
        data.refreshTokenExpiry
      );

      return { ...data, user: userWithAvatar };
    } catch (error) {
      console.error('❌ Google OAuth sync failed:', error);
      throw error;
    } finally {
      isSyncingOAuthRef.current = false;
    }
  }, []);

  // Setup activity tracking
  const setupActivityTracking = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start activity monitoring
    checkActivity();
  };

  // Remove activity listeners
  const removeActivityListeners = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    events.forEach(event => {
      document.removeEventListener(event, handleActivity, true);
    });
  };

  // Check if user has been active and extend session accordingly
  const checkActivity = () => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // If user has been active within the last hour and we have tokens
    if (timeSinceLastActivity < ACTIVITY_THRESHOLD && refreshToken) {
      // Extend the refresh token every hour of activity
      extendUserSession();
    }

    // Schedule next activity check
    activityTimeoutRef.current = setTimeout(checkActivity, ACTIVITY_THRESHOLD);
  };

  // Extend user session (refresh token expiry)
  const extendUserSession = async () => {
    if (!refreshToken || isRefreshingRef.current) return;
    try {
      isRefreshingRef.current = true;
      await publicApiPost('/auth/extend-session', { refreshToken });
    } catch (error) {
      console.error('❌ Failed to extend session:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  };

  // Schedule automatic token refresh
  const scheduleTokenRefresh = (accessTokenExpiry) => {
    clearTimeout(refreshTimeoutRef.current);

    const now = new Date();
    const timeUntilExpiry = accessTokenExpiry.getTime() - now.getTime();
    const refreshTime = Math.max(0, timeUntilExpiry - AUTO_REFRESH_BUFFER);

    refreshTimeoutRef.current = setTimeout(() => {
      if (refreshToken) {
        handleTokenRefresh(refreshToken, false);
      }
    }, refreshTime);
  };

  // Handle token refresh
  const handleTokenRefresh = async (currentRefreshToken, extendRefreshToken = false) => {
    if (isRefreshingRef.current) return;

    try {
      isRefreshingRef.current = true;

      const response = await publicApiPost('/auth/refresh', {
        refreshToken: currentRefreshToken,
        extendRefreshToken
      });

      if (response.ok) {
        const data = await response.json();

        // Update tokens and user data
        setAccessToken(data.accessToken);
        setUser(data.user);

        // Store in localStorage
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('accessTokenExpiry', data.accessTokenExpiry);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Schedule next refresh
        scheduleTokenRefresh(new Date(data.accessTokenExpiry));

        if (data.refreshTokenExtended) {
          // 
        }

      } else {
        await handleLogout();
      }

    } catch (error) {
      console.error('❌ Token refresh error:', error);
      await handleLogout();
    } finally {
      isRefreshingRef.current = false;
    }
  };

  /**
   * Internal method to set user data and tokens in state and local storage.
   * Also resets activity tracking and schedules the next token refresh.
   * 
   * @param {Object} userData - User profile data.
   * @param {string} userAccessToken - Short-lived JWT access token.
   * @param {string} userRefreshToken - Long-lived JWT refresh token.
   * @param {string} accessTokenExpiry - Expiration timestamp of the access token.
   * @param {string} refreshTokenExpiry - Expiration timestamp of the refresh token.
   */
  const loginUser = (userData, userAccessToken, userRefreshToken, accessTokenExpiry, refreshTokenExpiry) => {
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('accessToken', userAccessToken);
    localStorage.setItem('refreshToken', userRefreshToken);
    localStorage.setItem('accessTokenExpiry', accessTokenExpiry);
    localStorage.setItem('refreshTokenExpiry', refreshTokenExpiry);

    setUser(userData);
    setAccessToken(userAccessToken);
    setRefreshToken(userRefreshToken);

    // Schedule automatic refresh
    scheduleTokenRefresh(new Date(accessTokenExpiry));

    // Reset activity tracking
    lastActivityRef.current = Date.now();
  };

  /**
   * Logs out the current user, clears tokens, cancels refresh timers,
   * and signs out of Supabase to ensure complete session termination.
   * 
   * @returns {Promise<void>}
   */
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      const minWait = new Promise(resolve => setTimeout(resolve, 800)); // Smooth UX transition

      // Clear timers
      clearTimeout(refreshTimeoutRef.current);
      clearTimeout(activityTimeoutRef.current);

      // Revoke refresh token on server
      if (refreshToken) {
        await publicApiPost('/auth/logout', { refreshToken });
      }

      // Sign out from Supabase
      await supabase.auth.signOut();

      // Clear local storage
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('accessTokenExpiry');
      localStorage.removeItem('refreshTokenExpiry');

      // Clear state
      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);

      await minWait;
    } catch (error) {
      console.error('❌ Logout error:', error);

      // Clear local data anyway
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('accessTokenExpiry');
      localStorage.removeItem('refreshTokenExpiry');

      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);
    } finally {
      setIsLoggingOut(false);
    }
  };

  /**
   * Wrapper for `fetch` that automatically injects the Bearer access token
   * and the refresh token header. It also handles intercepting new tokens
   * sent by the server and handling 401 Unauthorized responses.
   * 
   * @param {string} url - The API endpoint to fetch.
   * @param {Object} options - Standard fetch options (method, headers, body).
   * @returns {Promise<Response>} The fetch response.
   * @throws {Error} If no access token is available or the request fails.
   */
  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const requestOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${accessToken}`,
        'x-refresh-token': refreshToken,
      },
    };

    try {
      const response = await fetch(url, requestOptions);

      // Check if server sent a new access token (auto-refresh happened)
      const newAccessToken = response.headers.get('x-new-access-token');
      const newAccessTokenExpiry = response.headers.get('x-access-token-expiry');

      if (newAccessToken && newAccessTokenExpiry) {
        // Update tokens
        setAccessToken(newAccessToken);
        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('accessTokenExpiry', newAccessTokenExpiry);

        // Schedule next refresh
        scheduleTokenRefresh(new Date(newAccessTokenExpiry));
      }

      // Check if response indicates we need to login again
      if (response.status === 401) {
        const errorData = await response.json();
        if (errorData.needsLogin) {
          await handleLogout();
          // Redirect to home page
          window.location.href = '/';
          return;
        }
      }

      return response;

    } catch (error) {
      console.error('❌ Authenticated request failed:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Check for existing tokens when the app loads
    const savedAccessToken = localStorage.getItem('accessToken');
    const savedRefreshToken = localStorage.getItem('refreshToken');
    const savedUser = localStorage.getItem('user');
    const savedAccessTokenExpiry = localStorage.getItem('accessTokenExpiry');

    if (savedAccessToken && savedRefreshToken && savedUser) {
      const accessTokenExpiry = savedAccessTokenExpiry ? new Date(savedAccessTokenExpiry) : null;
      const now = new Date();

      setUser(JSON.parse(savedUser));
      setRefreshToken(savedRefreshToken);

      // Check if access token is still valid or needs refresh
      if (accessTokenExpiry && accessTokenExpiry > now) {
        setAccessToken(savedAccessToken);
        scheduleTokenRefresh(accessTokenExpiry);
      } else {
        // Access token expired, try to refresh immediately
        handleTokenRefresh(savedRefreshToken, false);
      }
    }

    // =========================================================================
    // 🆕 GOOGLE OAUTH — Listen for Supabase auth state changes
    // =========================================================================
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // Only sync if we don't already have our custom JWT tokens stored
          const existingAccessToken = localStorage.getItem('accessToken');
          if (!existingAccessToken) {
            try {
              const syncResult = await syncGoogleUser(session.user);
              // Check if user needs to set username (null = first-time Google user)
              const destination = (syncResult?.user?.username === null || syncResult?.user?.username === '')
                ? '/set-username'
                : '/dashboard';
              window.history.pushState({}, '', destination);
              window.dispatchEvent(new PopStateEvent('popstate'));
            } catch (error) {
              console.error('❌ Failed to sync Google user:', error);
            }
          }
        }
      }
    );

    setLoading(false);
    setupActivityTracking();

    // Cleanup on unmount
    return () => {
      clearTimeout(refreshTimeoutRef.current);
      clearTimeout(activityTimeoutRef.current);
      removeActivityListeners();
      subscription?.unsubscribe();
    };
  }, [syncGoogleUser]);

  // Check if user is authenticated
  const isAuthenticated = !!accessToken && !!user;

  // Check if user needs to set a username (first-time Google OAuth user)
  const needsUsername = isAuthenticated && (user?.username === null || user?.username === '' || !user?.username);

  // Provide context value
  const contextValue = {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    needsUsername,
    isLoggingOut,
    login: loginUser,
    logout: handleLogout,
    loading,
    makeAuthenticatedRequest,
    // 🆕 Google OAuth
    signInWithGoogle,

    // Utility functions
    extendUserSession,
    handleTokenRefresh: (extend = false) => handleTokenRefresh(refreshToken, extend)
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to consume the AuthContext.
 * Ensures that the component is wrapped in an AuthProvider.
 * 
 * @returns {Object} The authentication context value containing user state and auth methods.
 * @throws {Error} If called outside of AuthProvider.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};